import type { DocumentChangedAction } from '@Application/Beneficiaries/Documents/Actions/documentChanged';
import type { DocumentRemovedAction } from '@Application/Beneficiaries/Documents/Actions/documentRemoved';
import type { ImportedDocumentsChangedAction } from '@Application/Beneficiaries/ImportedDocuments/Actions/importedDocumentsChanged';
import type { InterviewChangedAction } from '@Application/Beneficiaries/Interviews/Actions/interviewChanged';
import type { InterviewRemovedAction } from '@Application/Beneficiaries/Interviews/Actions/interviewRemoved';
import subscribeToEventStream, {
	type EventStreamOpenedAction,
	type EventStreamSubscription,
} from '@Application/Realtime/subscribeToEventStream';
import type SubscribeToSubjectEvents from '@Application/Subjects/Queries/SubscribeToSubjectEvents';
import type { WorkflowRunChangedAction } from '@Application/WorkflowRuns/WorkflowRun/Actions/workflowRunChanged';
import type { WorkflowRunRemovedAction } from '@Application/WorkflowRuns/WorkflowRun/Actions/workflowRunRemoved';

export type SubjectEventAction =
	| WorkflowRunChangedAction
	| WorkflowRunRemovedAction
	| InterviewChangedAction
	| InterviewRemovedAction
	| DocumentChangedAction
	| DocumentRemovedAction
	| ImportedDocumentsChangedAction;

const SUBJECT_EVENT_TYPES: readonly SubjectEventAction['type'][] = [
	'WORKFLOW_RUN_CHANGED',
	'WORKFLOW_RUN_REMOVED',
	'INTERVIEW_CHANGED',
	'INTERVIEW_REMOVED',
	'DOCUMENT_CHANGED',
	'DOCUMENT_REMOVED',
	'IMPORTED_DOCUMENTS_CHANGED',
];

/**
 * Follows the dossier's event stream. The transport delivers each frame as `{ type, payload }`; the
 * address of the dossier is added here, so the store handlers can compare it to the list they hold.
 */
export default (query: SubscribeToSubjectEvents): EventStreamSubscription<SubjectEventAction> => {
	const subscription = subscribeToEventStream<SubjectEventAction>(
		query.url,
		SUBJECT_EVENT_TYPES,
		query.reconnectionUrl,
	);

	return {
		close: subscription.close,
		start: (dispatch) =>
			subscription.start((action: SubjectEventAction | EventStreamOpenedAction) => {
				if (action.type === 'EVENT_STREAM_OPENED') {
					dispatch(action);
					return;
				}

				dispatch({
					...action,
					typeKey: query.subject.typeKey,
					subjectId: query.subject.subjectId,
				});
			}),
	};
};
